import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useAuth } from '@features/auth/contexts/AuthContext';
import { Button } from "@shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@shared/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { Input } from "@shared/components/ui/input";
import { Textarea } from "@shared/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@shared/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@shared/components/ui/table";
import { Badge } from "@shared/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import {
  Package,
  Plus,
  Minus,
  Edit,
  Upload,
  Download,
  Trash2,
  AlertTriangle,
  CheckCircle,
  XCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { bulkUpdateProductStock } from '@features/products/services/productService';
import { SelectProduct } from "./SelectProduct";
import { VALIDATION_MESSAGES } from "@shared/utils/constants";

const operationSchema = z.object({
  operation: z.enum(["set", "add", "subtract", "multiply", "percentage"]),
  value: z.number().min(0, VALIDATION_MESSAGES.NON_NEGATIVE),
  reason: z.string().min(1, VALIDATION_MESSAGES.REQUIRED).max(255),
  adjustmentType: z.enum(["manual", "correction"]).default("manual"),
});

const bulkUpdateSchema = z.object({
  products: z.array(z.object({
    productId: z.string().min(1, VALIDATION_MESSAGES.REQUIRED),
    currentStock: z.number(),
    newStock: z.number().min(0),
    productName: z.string(),
  })).min(1, "At least one product is required"),
  reason: z.string().min(1, VALIDATION_MESSAGES.REQUIRED).max(255),
  adjustmentType: z.enum(["manual", "correction"]).default("manual"),
});

export function BulkStockManager({ trigger }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [operationMode, setOperationMode] = useState("individual");
  const queryClient = useQueryClient();
  
  // Check if user can edit stock movements (admin or manager only)
  const canEditStock = user?.role === "admin" || user?.role === "manager";

  // If user doesn't have permission, don't render the component
  if (!canEditStock) {
    return null;
  }

  const operationForm = useForm({
    resolver: zodResolver(operationSchema),
    defaultValues: {
      operation: "set",
      value: 0,
      reason: "",
      adjustmentType: "manual",
    },
  });

  const bulkUpdateForm = useForm({
    resolver: zodResolver(bulkUpdateSchema),
    defaultValues: {
      products: [],
      reason: "",
      adjustmentType: "manual",
    },
  });

  const bulkUpdateMutation = useMutation({
    mutationFn: async (data) => {
      const stockUpdates = data.products.map(product => ({
        productId: product.productId,
        newStock: product.newStock,
      }));
      
      return await bulkUpdateProductStock(stockUpdates, data.reason);
    },
    onSuccess: (data) => {
      setOpen(false);
      toast.success(t("products.bulkStockUpdateSuccess", { count: data.results?.length || 0 }));
      queryClient.invalidateQueries(["products"]);
      queryClient.invalidateQueries(["stockSummary"]);
      resetForms();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || t("products.bulkStockUpdateFailed"));
    },
  });

  const resetForms = () => {
    operationForm.reset();
    bulkUpdateForm.reset();
    setSelectedProducts([]);
  };

  const handleProductSelect = (product) => {
    if (!selectedProducts.find(p => p.productId === product._id)) {
      const newProduct = {
        productId: product._id,
        productName: product.name,
        currentStock: product.stock || 0,
        newStock: product.stock || 0,
      };
      setSelectedProducts([...selectedProducts, newProduct]);
      bulkUpdateForm.setValue("products", [...selectedProducts, newProduct]);
    }
  };

  const handleRemoveProduct = (productId) => {
    const updatedProducts = selectedProducts.filter(p => p.productId !== productId);
    setSelectedProducts(updatedProducts);
    bulkUpdateForm.setValue("products", updatedProducts);
  };

  const handleOperationChange = (operation, value) => {
    const newProducts = selectedProducts.map(product => {
      let newStock = product.currentStock;
      
      switch (operation) {
        case "set":
          newStock = value;
          break;
        case "add":
          newStock = product.currentStock + value;
          break;
        case "subtract":
          newStock = Math.max(0, product.currentStock - value);
          break;
        case "multiply":
          newStock = Math.round(product.currentStock * value);
          break;
        case "percentage":
          newStock = Math.round(product.currentStock * (value / 100));
          break;
        default:
          newStock = product.currentStock;
      }
      
      return { ...product, newStock: Math.max(0, newStock) };
    });
    
    setSelectedProducts(newProducts);
    bulkUpdateForm.setValue("products", newProducts);
  };

  const handleBulkUpdate = (data) => {
    bulkUpdateMutation.mutate(data);
  };

  const handleOperationUpdate = (data) => {
    const products = selectedProducts.map(product => ({
      productId: product.productId,
      newStock: product.newStock,
    }));
    
    bulkUpdateMutation.mutate({
      products,
      reason: data.reason,
      adjustmentType: data.adjustmentType,
    });
  };

  const getOperationDescription = (operation) => {
    switch (operation) {
      case "set": return t("products.operationSet");
      case "add": return t("products.operationAdd");
      case "subtract": return t("products.operationSubtract");
      case "multiply": return t("products.operationMultiply");
      case "percentage": return t("products.operationPercentage");
      default: return "";
    }
  };

  const getStockChangeColor = (current, newStock) => {
    if (newStock > current) return "text-green-600";
    if (newStock < current) return "text-red-600";
    return "text-gray-600";
  };

  const getStockChangeIcon = (current, newStock) => {
    if (newStock > current) return <Plus className="w-4 h-4 text-green-600" />;
    if (newStock < current) return <Minus className="w-4 h-4 text-red-600" />;
    return <CheckCircle className="w-4 h-4 text-gray-600" />;
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger || (
          <Button variant="outline">
            <Package className="w-4 h-4 mr-2" />
            {t("products.bulkStockUpdate")}
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Package className="w-5 h-5" />
            {t("products.bulkStockManagement")}
          </DialogTitle>
          <DialogDescription>
            {t("products.bulkStockManagementDescription")}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Product Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                {t("products.selectProducts")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <SelectProduct
                value={null}
                onValueChange={handleProductSelect}
                placeholder={t("products.searchAndSelectProducts")}
              />
              
              {selectedProducts.length > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      {t("products.selectedProducts")} ({selectedProducts.length})
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setSelectedProducts([]);
                        bulkUpdateForm.setValue("products", []);
                      }}
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      {t("common.clear")}
                    </Button>
                  </div>
                  
                  <div className="border rounded-lg overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>{t("products.product")}</TableHead>
                          <TableHead>{t("products.currentStock")}</TableHead>
                          <TableHead>{t("products.newStock")}</TableHead>
                          <TableHead>{t("products.change")}</TableHead>
                          <TableHead>{t("common.actions")}</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {selectedProducts.map((product) => (
                          <TableRow key={product.productId}>
                            <TableCell>
                              <div className="font-medium">{product.productName}</div>
                              <div className="text-sm text-muted-foreground">
                                ID: {product.productId.slice(-6)}
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="font-medium">{product.currentStock}</span>
                            </TableCell>
                            <TableCell>
                              <Input
                                type="number"
                                min="0"
                                value={product.newStock}
                                onChange={(e) => {
                                  const newStock = parseInt(e.target.value) || 0;
                                  const updatedProducts = selectedProducts.map(p =>
                                    p.productId === product.productId
                                      ? { ...p, newStock }
                                      : p
                                  );
                                  setSelectedProducts(updatedProducts);
                                  bulkUpdateForm.setValue("products", updatedProducts);
                                }}
                                className="w-20"
                              />
                            </TableCell>
                            <TableCell>
                              <div className={`flex items-center gap-1 ${getStockChangeColor(product.currentStock, product.newStock)}`}>
                                {getStockChangeIcon(product.currentStock, product.newStock)}
                                <span className="font-medium">
                                  {product.newStock - product.currentStock > 0 ? "+" : ""}
                                  {product.newStock - product.currentStock}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleRemoveProduct(product.productId)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Operation Mode Selection */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Edit className="w-5 h-5" />
                {t("products.updateMethod")}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Button
                  variant={operationMode === "individual" ? "default" : "outline"}
                  onClick={() => setOperationMode("individual")}
                  className="justify-start"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  {t("products.individualUpdates")}
                </Button>
                <Button
                  variant={operationMode === "bulk" ? "default" : "outline"}
                  onClick={() => setOperationMode("bulk")}
                  className="justify-start"
                >
                  <Package className="w-4 h-4 mr-2" />
                  {t("products.bulkOperation")}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Individual Updates */}
          {operationMode === "individual" && selectedProducts.length > 0 && (
            <Form {...bulkUpdateForm}>
              <form onSubmit={bulkUpdateForm.handleSubmit(handleBulkUpdate)} className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Edit className="w-5 h-5" />
                      {t("products.updateDetails")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <FormField
                      control={bulkUpdateForm.control}
                      name="reason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("products.reason")}</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={t("products.reasonPlaceholder")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    
                    <FormField
                      control={bulkUpdateForm.control}
                      name="adjustmentType"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("products.adjustmentType")}</FormLabel>
                          <Select onValueChange={field.onChange} defaultValue={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="manual">{t("products.adjustmentTypes.manual")}</SelectItem>
                              <SelectItem value="correction">{t("products.adjustmentTypes.correction")}</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </form>
            </Form>
          )}

          {/* Bulk Operation */}
          {operationMode === "bulk" && selectedProducts.length > 0 && (
            <Form {...operationForm}>
              <form onSubmit={operationForm.handleSubmit(handleOperationUpdate)} className="space-y-4">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      {t("products.bulkOperation")}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        control={operationForm.control}
                        name="operation"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("products.operation")}</FormLabel>
                            <Select onValueChange={(value) => {
                              field.onChange(value);
                              handleOperationChange(value, operationForm.getValues("value"));
                            }} defaultValue={field.value}>
                              <FormControl>
                                <SelectTrigger>
                                  <SelectValue />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                <SelectItem value="set">{t("products.operationSet")}</SelectItem>
                                <SelectItem value="add">{t("products.operationAdd")}</SelectItem>
                                <SelectItem value="subtract">{t("products.operationSubtract")}</SelectItem>
                                <SelectItem value="multiply">{t("products.operationMultiply")}</SelectItem>
                                <SelectItem value="percentage">{t("products.operationPercentage")}</SelectItem>
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      
                      <FormField
                        control={operationForm.control}
                        name="value"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("products.value")}</FormLabel>
                            <FormControl>
                              <Input
                                type="number"
                                min="0"
                                step={operationForm.watch("operation") === "percentage" ? "0.1" : "1"}
                                {...field}
                                onChange={(e) => {
                                  const value = parseFloat(e.target.value) || 0;
                                  field.onChange(value);
                                  handleOperationChange(operationForm.watch("operation"), value);
                                }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    
                    <Alert>
                      <AlertTriangle className="h-4 w-4" />
                      <AlertDescription>
                        {getOperationDescription(operationForm.watch("operation"))}
                      </AlertDescription>
                    </Alert>
                    
                    <FormField
                      control={operationForm.control}
                      name="reason"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("products.reason")}</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder={t("products.reasonPlaceholder")}
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </CardContent>
                </Card>
              </form>
            </Form>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            {t("common.cancel")}
          </Button>
          {selectedProducts.length > 0 && (
            <Button
              onClick={() => {
                if (operationMode === "individual") {
                  bulkUpdateForm.handleSubmit(handleBulkUpdate)();
                } else {
                  operationForm.handleSubmit(handleOperationUpdate)();
                }
              }}
              disabled={bulkUpdateMutation.isPending}
            >
              {bulkUpdateMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  {t("products.updating")}
                </>
              ) : (
                <>
                  <Package className="w-4 h-4 mr-2" />
                  {t("products.updateStock")}
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
} 